/* eslint-disable no-console, max-lines*/
/* global google */

import { getMarkerIcon } from './helpers/markerIcons';

// Constants
const MOBILE_BREAKPOINT = 992;
const DEFAULT_BATCH_SIZE = 50;
const DEFAULT_MIN_VISIBLE_RESULTS = 10;
const DEFAULT_ZOOM_MAX = 16;
const AREA_SEARCH_PROMPT_SUPPRESSION_MS = 800;
const INFINITE_SCROLL_SUPPRESSION_MS = 500;
const MARKER_CLICK_SCROLL_SUPPRESSION_MS = 700;
const SCROLL_TO_DEALER_SUPPRESSION_MS = 450;
const SCROLL_TO_DEALER_ANIMATION_MS = 300;
const POST_SCROLL_SUPPRESSION_MS = 150;

/**
 * Parses a positive integer config value from a data attribute.
 * @param {string|undefined} value - Raw attribute value
 * @param {number} fallback - Fallback value
 * @returns {number} Parsed positive integer or fallback
 */
const parsePositiveIntegerConfig = (value, fallback) => {
  const parsedValue = Number.parseInt(value, 10);

  return parsedValue > 0 ? parsedValue : fallback;
};

/**
 * Parses a boolean config value from a data attribute.
 * @param {string|undefined} value - Raw attribute value
 * @param {boolean} fallback - Fallback value
 * @returns {boolean} Parsed boolean or fallback
 */
const parseBooleanConfig = (value, fallback) => {
  if (value === undefined) {
    return fallback;
  }

  return String(value).toLowerCase() !== 'false';
};

/**
 * Checks if the current viewport is mobile
 * @returns {boolean} True if viewport width is <= MOBILE_BREAKPOINT
 */
const isMobileViewport = () => window.innerWidth <= MOBILE_BREAKPOINT;

export class DealerLocatorMap {
  /**
   * Checks if the dealer locator map is on the page.
   * @returns {boolean} True if the dealer locator is on the page, false otherwise.
   */
  static isOnPage() {
    return $('.dealer-locator').length > 0;
  }

  /**
   * Initializes the DealerLocatorMap instance.
   */
  constructor() {
    const $dealerLocator = $('.dealer-locator');

    this.map = null;
    this.infoWindow = null;
    this.selectedLocation = null;
    this.locations = [];
    this.markerLocationPairs = [];
    this.userLocationMarker = null;
    this.geolocationRequested = false;
    this.currentVisibleDealers = [];
    this.displayedDealersList = [];
    this.filteredDealers = [];
    this.displayedCount = 0;
    this.dealersPerPage = parsePositiveIntegerConfig($dealerLocator.attr('data-batch-size'), DEFAULT_BATCH_SIZE);
    this.minVisibleResults = parsePositiveIntegerConfig($dealerLocator.attr('data-min-visible-results'), DEFAULT_MIN_VISIBLE_RESULTS);
    this.fillMinResults = parseBooleanConfig($dealerLocator.attr('data-fill-min-results'), true);
    this.currentSort = 'distance';
    this.activeFilters = [];
    this.markersCreated = false;
    this.filterEventsAttached = false;
    this.selectedBounds = null;
    this.searchAreaBounds = null;
    this.searchAreaCenter = null;
    this.hasActiveSearch = false;
    this.mapAreaEventsAttached = false;
    this.suppressAreaSearchPrompt = false;
    this.areaSearchPromptTimeout = null;
    this.suppressInfiniteScrollLoad = false;
    this.suppressInfiniteScrollTimeout = null;
    this.isLoading = false;
    this.infiniteScrollAttached = false;
    this.distanceDisplay = $('.dealer-locator').data('distance-display') || $('.dealer-locator').data('distance-unit') || 'km';
    this.attachEvents();
    this.initDealerListEventListeners();
  }

  /**
   * Initializes the Google Map, sets the default center, zoom and country code, fetches dealer locations,
   * and enables search functionality for locating dealers.
   */
  initMap() {
    const defaultCenter = this.getCountryCenter();

    this.map = new google.maps.Map($('.dealer-locator-map')[0], {
      center: defaultCenter.center,
      zoom: defaultCenter.zoom,
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
    });
    this.attachMapAreaEvents();

    this.infoWindow = new google.maps.InfoWindow();
    const apiRoot = $('.dealer-locator').data('api-root') || '';

    $.ajax({
      url: `${apiRoot}/Dealers`,
      method: 'GET',
      dataType: 'json',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
      },
    })
      .done((data) => {
        this.locations = data;
        this.createMarkers(false);
      });

    this.initAutocomplete();
  }

  /**
   * Prevents programmatic map movements from showing the "Search in this area" prompt.
   * @param {Function} callback - Map update callback to run while prompt is suppressed
   */
  runProgrammaticMapUpdate(callback) {
    this.suppressAreaSearchPrompt = true;
    window.clearTimeout(this.areaSearchPromptTimeout);

    callback();

    if (this.map) {
      google.maps.event.addListenerOnce(this.map, 'idle', () => {
        this.suppressAreaSearchPrompt = false;
      });
    }

    this.areaSearchPromptTimeout = window.setTimeout(() => {
      this.suppressAreaSearchPrompt = false;
    }, AREA_SEARCH_PROMPT_SUPPRESSION_MS);
  }

  /**
   * Shows the map-area search button after the user changes the visible map area.
   */
  showSearchAreaButton() {
    if (this.suppressAreaSearchPrompt || !this.hasActiveSearch) {
      return;
    }

    if (!$('.dealer-locator-search-results').is(':visible')) {
      return;
    }

    if (isMobileViewport() && $('.dealer-locator-map').hasClass('mobile-hidden')) {
      return;
    }

    $('.dealer-locator-search-area-btn')
      .addClass('is-visible')
      .attr('aria-hidden', 'false');
  }

  /**
   * Hides the map-area search button.
   */
  hideSearchAreaButton() {
    $('.dealer-locator-search-area-btn')
      .removeClass('is-visible')
      .attr('aria-hidden', 'true');
  }

  /**
   * Temporarily disables infinite scroll while the UI performs internal list movement.
   * @param {number} duration - Suppression duration in milliseconds
   */
  suppressInfiniteScroll(duration = INFINITE_SCROLL_SUPPRESSION_MS) {
    this.suppressInfiniteScrollLoad = true;
    window.clearTimeout(this.suppressInfiniteScrollTimeout);
    this.suppressInfiniteScrollTimeout = window.setTimeout(() => {
      this.suppressInfiniteScrollLoad = false;
    }, duration);
  }

  /**
   * Attaches user-driven map move/zoom listeners for the "Search in this area" CTA.
   */
  attachMapAreaEvents() {
    if (this.mapAreaEventsAttached || !this.map) {
      return;
    }

    this.mapAreaEventsAttached = true;

    this.map.addListener('dragend', () => {
      this.showSearchAreaButton();
    });

    this.map.addListener('zoom_changed', () => {
      this.showSearchAreaButton();
    });
  }

  /**
   * Initializes Google Maps Places Autocomplete functionality for the dealer locator search input.
   *
   * This method sets up autocomplete with country-specific restrictions, manages session tokens
   * for billing optimization, and handles user interactions including geolocation requests.
   * It validates that selected places match the configured country restriction before accepting them.
   *
   * @returns {void}
   *
   * @listens click - On search input to request user's geolocation
   * @listens keydown - On search input to prevent form submission when autocomplete dropdown is visible
   * @listens place_changed - On autocomplete to validate and store the selected location
   *
   * @see {@link https://developers.google.com/maps/documentation/javascript/place-autocomplete|Google Places Autocomplete}
   */
  initAutocomplete() {
    const countryData = this.getCountryCenter();
    const countryCode = countryData.countryCode;
    const input = document.querySelector('.dealer-locator-search-input');
    let sessionToken = new google.maps.places.AutocompleteSessionToken();
    const options = {
      fields: ['address_components', 'geometry', 'name'],
      sessionToken: sessionToken
    };

    if (countryCode && typeof countryCode === 'string') {
      options.componentRestrictions = { country: countryCode };
    }

    const autocomplete = new google.maps.places.Autocomplete(input, options);

    $(input).on('click', () => {
      this.requestGeolocation(input);
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();

      sessionToken = new google.maps.places.AutocompleteSessionToken();
      autocomplete.setOptions({ sessionToken: sessionToken });

      if (options.componentRestrictions && place.address_components) {
        const countryComponent = place.address_components.find(component => component.types.includes('country'));
        const expectedCountryCode = options.componentRestrictions.country;

        if (!countryComponent || countryComponent.short_name.toLowerCase() !== expectedCountryCode) {
          input.value = '';
          this.selectedLocation = null;
          this.selectedBounds = null;

          return;
        }
      }

      if (place.geometry && place.geometry.location) {
        this.selectedLocation = place.geometry.location;
        this.selectedBounds = place.geometry.viewport || null;
      } else {
        this.selectedLocation = null;
        this.selectedBounds = null;
      }
    });
  }

  /**
   * Requests user's geolocation and updates the map with their current position.
   * The user location marker is styled with a red circle icon to distinguish it from dealer markers.
   * If geolocation fails or is denied, the method logs a warning and resets the geolocation flag.
   *
   * @param {HTMLElement} input - The search input field where the geocoded address will be displayed
   * @returns {void}
   *
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Geolocation_API|MDN Geolocation API}
   */
  requestGeolocation(input) {
    if (this.geolocationRequested) {
      return;
    }

    this.geolocationRequested = true;

    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        this.selectedLocation = new google.maps.LatLng(userLocation.lat, userLocation.lng);

        if (this.userLocationMarker) {
          this.userLocationMarker.setMap(null);
        }

        this.userLocationMarker = new google.maps.Marker({
          position: this.selectedLocation,
          map: this.map,
          title: 'Your Location',
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#D92912',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3
          },
          zIndex: 1000
        });

        this.runProgrammaticMapUpdate(() => {
          this.map.setCenter(this.selectedLocation);
          this.map.setZoom(13);
        });

        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: this.selectedLocation }, (results, status) => {
          if (status === 'OK' && results[0]) {
            input.value = results[0].formatted_address;
            $('.dealer-locator-search-input-wrapper .dealer-locator-search-icon').hide();
            $('.dealer-locator-search-clear').addClass('is-active');
          }
        });
      },
      () => {
        this.geolocationRequested = false;
      }
    );
  }

  /**
   * Clears all map markers.
   */
  clearMarkers() {
    this.markerLocationPairs.forEach(({ marker }) => {
      marker.setMap(null);
    });
  }

  /**
   * Retrieves the default map center, zoom level, and country code based on the hostname.
   * Prioritizes specific hostnames (like jobst-usa.com, jobstcanada.com)
   * then falls back to checking the top-level domain (TLD).
   * Defaults to a world view if no specific country is matched.
   *
   * Supported locations derived from hostnames/TLDs:
   * USA, Germany, France, Poland, Italy, United Kingdom, Portugal, Canada,
   * Spain, Czech Republic, Belgium, Austria, Sweden, Norway, Australia.
   *
   * @returns {Object} An object containing:
   *   - center: { lat: number, lng: number } - The map center coordinates.
   *   - zoom: number - The map zoom level.
   *   - countryCode: string | null - The ISO 3166-1 alpha-2 country code (e.g., 'us', 'de', 'gb') or null for the default world view.
   */
  getCountryCenter() {
    const hostname = window.location.hostname.toLowerCase();

    // Map keys can be full hostnames or TLDs.
    // Full hostnames will be checked first for higher specificity.
    const countryDataMap = {
      // -- Specific Hostnames (Checked First) --
      'jobst-usa.com': { center: { lat: 37.0902, lng: -95.7129 }, zoom: 5, countryCode: 'us' }, // USA
      'jobstcanada.com': { center: { lat: 56.1304, lng: -106.3468 }, zoom: 4, countryCode: 'ca' }, // Canada

      // -- Top-Level Domains (Checked Second) --
      'de': { center: { lat: 51.1657, lng: 10.4515 }, zoom: 6, countryCode: 'de' }, // Germany
      'fr': { center: { lat: 46.6034, lng: 1.8883 }, zoom: 6, countryCode: 'fr' }, // France
      'pl': { center: { lat: 51.9194, lng: 19.1451 }, zoom: 6, countryCode: 'pl' }, // Poland
      'it': { center: { lat: 41.8719, lng: 12.5674 }, zoom: 6, countryCode: 'it' }, // Italy
      'uk': { center: { lat: 55.3781, lng: -3.4360 }, zoom: 6, countryCode: 'gb' }, // United Kingdom (Note: TLD is .uk, ISO code is GB)
      'pt': { center: { lat: 39.3999, lng: -8.2245 }, zoom: 6, countryCode: 'pt' }, // Portugal
      'es': { center: { lat: 40.4637, lng: -3.7492 }, zoom: 6, countryCode: 'es' }, // Spain
      'cz': { center: { lat: 49.8175, lng: 15.4730 }, zoom: 7, countryCode: 'cz' }, // Czech Republic
      'be': { center: { lat: 50.8503, lng: 4.3517 }, zoom: 8, countryCode: 'be' }, // Belgium
      'at': { center: { lat: 47.5162, lng: 14.5501 }, zoom: 7, countryCode: 'at' }, // Austria
      'se': { center: { lat: 60.1282, lng: 18.6435 }, zoom: 5, countryCode: 'se' }, // Sweden
      'no': { center: { lat: 60.4720, lng: 8.4689 }, zoom: 5, countryCode: 'no' }, // Norway
      'au': { center: { lat: -25.2744, lng: 133.7751 }, zoom: 4, countryCode: 'au' }, // Australia
      'fi': { center: { lat: 64.0000, lng: 26.0000 }, zoom: 5, countryCode: 'fi' }, // Finland
      'nl': { center: { lat: 52.1326, lng: 5.2913 }, zoom: 8, countryCode: 'nl' }, // Netherlands
    };

    // Default Settings (World View)
    const defaultData = {
      center: { lat: 20, lng: 0 },
      zoom: 2,
      countryCode: null
    };

    // 1. Match by domain suffix (e.g. endsWith jobst-usa.com or jobstcanada.com)
    for (const domain in countryDataMap) {
      if (hostname === domain || hostname.endsWith(`.${domain}`)) {
        return countryDataMap[domain];
      }
    }

    // 2. If no full hostname match, check the Top-Level Domain (TLD)
    const domainParts = hostname.split('.');
    const tld = domainParts.length > 1 ? domainParts[domainParts.length - 1] : null;

    if (tld && countryDataMap[tld]) {
      return countryDataMap[tld];
    }

    // 3. Return default if no specific match found
    return defaultData;
  }

  /**
   * Creates markers for all locations on the map.
   * @param {boolean} showMarkers - Whether to display the markers on the map.
   */
  createMarkers(showMarkers) {
    if (this.markersCreated && this.markerLocationPairs.length > 0) {
      return;
    }

    for (const { marker } of this.markerLocationPairs) {
      marker.setMap(null);
    }
    this.markerLocationPairs = [];

    // Set flag immediately to prevent race condition
    this.markersCreated = true;

    for (const location of this.locations) {
      try {
        const lat = Number.parseFloat(location.Latitude);
        const lng = Number.parseFloat(location.Longitude);

        if (Number.isNaN(lat) || Number.isNaN(lng)) {
          continue;
        }

        const latLng = new google.maps.LatLng(lat, lng);

        const marker = new google.maps.Marker({
          position: latLng,
          map: showMarkers ? this.map : null,
          title: location.Name || 'Unknown Dealer',
          icon: getMarkerIcon('blue')
        });

        this.markerLocationPairs.push({ marker, location });

        marker.addListener('click', () => {
          this.handleMarkerClick(marker, location);
        });
      } catch (error) {
        // Skip dealers with invalid data (malformed lat/lng, missing required fields, etc.)
      }
    }
  }

  /**
   * Handles marker click event
   * - On desktop: expands dealer in list and scrolls to it
   * - On mobile: shows mobile info panel
   * @param {google.maps.Marker} marker - The clicked marker
   * @param {Object} location - The dealer location data
   */
  handleMarkerClick(marker, location) {
    const markerIndex = this.markerLocationPairs.findIndex(pair => pair.marker === marker);
    const dealerData = this.currentVisibleDealers.find(dealer => dealer.location === location);
    const distance = dealerData ? dealerData.distance : 0;

    this.markerLocationPairs.forEach(pair => {
      this.setMarkerColor(pair.marker, 'blue');
    });

    this.setMarkerColor(marker, 'red');

    if (isMobileViewport()) {
      this.showMobileInfoPanel(location, distance);
    } else {
      this.suppressInfiniteScroll(MARKER_CLICK_SCROLL_SUPPRESSION_MS);
      const $resultsList = $('.dealer-locator-search-results-list');
      const $dealerItem = $resultsList.find(`.dealer-locator-view-marker-btn[data-marker-index="${markerIndex}"]`).closest('.dealer-list-item');

      if ($dealerItem.length > 0) {
        this.expandDealerInList(markerIndex);
      }
    }
  }

  /**
   * Activates the first (closest) dealer immediately after search
   * - Desktop: Expands first dealer in list + red marker
   * - Mobile: Only red marker (no info card)
   */
  activateFirstDealer() {
    if (this.displayedDealersList.length === 0) {
      return;
    }

    const firstDealer = this.displayedDealersList[0];
    const markerIndex = this.markerLocationPairs.findIndex(pair => pair.marker === firstDealer.marker);

    if (markerIndex === -1) {
      return;
    }

    this.setMarkerColor(firstDealer.marker, 'red');

    if (!isMobileViewport()) {
      setTimeout(() => {
        this.expandDealerInList(markerIndex);
      }, 350);
    }
  }

  /**
   * Expands a dealer in the list by marker index and scrolls to it if not visible
   * @param {number} markerIndex - Index of the marker in markerLocationPairs
   * @param {boolean} updateMarkerColor - Whether to update marker colors (default: true for desktop, false for mobile)
   */
  expandDealerInList(markerIndex, updateMarkerColor = !isMobileViewport()) {
    const $resultsList = $('.dealer-locator-search-results-list');
    const $dealerItem = $resultsList.find(`.dealer-locator-view-marker-btn[data-marker-index="${markerIndex}"]`).closest('.dealer-list-item');

    if ($dealerItem.length === 0) {
      return;
    }

    // If this dealer is already active, just ensure marker is red (if applicable) and scroll to it
    if ($dealerItem.hasClass('dealer-list-item--active')) {
      if (updateMarkerColor && this.markerLocationPairs[markerIndex]) {
        this.setMarkerColor(this.markerLocationPairs[markerIndex].marker, 'red');
      }
      this.scrollToDealerItem($dealerItem, $resultsList);

      return;
    }

    const $openItems = $('.dealer-list-item--active');
    $openItems.each((index, el) => {
      const $otherItem = $(el);
      $otherItem.removeClass('dealer-list-item--active');
      $otherItem.find('[data-dealer-content="main"]').slideUp(300);
      $otherItem.find('.dealer-chevron').removeClass('dealer-chevron--open');

      if (updateMarkerColor) {
        const otherIdx = $otherItem.find('.dealer-locator-view-marker-btn').first().data('marker-index');
        if (otherIdx !== undefined && this.markerLocationPairs[otherIdx]) {
          this.setMarkerColor(this.markerLocationPairs[otherIdx].marker, 'blue');
        }
      }
    });

    $dealerItem.addClass('dealer-list-item--active');
    $dealerItem.find('[data-dealer-content="main"]').slideDown(300);
    $dealerItem.find('.dealer-chevron').addClass('dealer-chevron--open');

    if (updateMarkerColor && this.markerLocationPairs[markerIndex]) {
      this.setMarkerColor(this.markerLocationPairs[markerIndex].marker, 'red');
    }

    this.scrollToDealerItem($dealerItem, $resultsList);
  }

  /**
   * Scrolls to a dealer item if it's not visible in the results list viewport
   * @param {jQuery} $dealerItem - The dealer list item element
   * @param {jQuery} $resultsList - The results list container
   */
  scrollToDealerItem($dealerItem, $resultsList) {
    const listTop = $resultsList.offset().top;
    const listHeight = $resultsList.outerHeight();
    const listBottom = listTop + listHeight;
    const itemTop = $dealerItem.offset().top;
    const itemHeight = $dealerItem.outerHeight();
    const itemBottom = itemTop + itemHeight;

    if (itemTop < listTop || itemBottom > listBottom) {
      const scrollTop = $resultsList.scrollTop();
      const itemOffsetTop = itemTop - listTop + scrollTop;
      const targetScroll = itemOffsetTop - (listHeight / 2) + (itemHeight / 2);

      this.suppressInfiniteScroll(SCROLL_TO_DEALER_SUPPRESSION_MS);

      $resultsList.stop(true).animate({
        scrollTop: Math.max(0, targetScroll)
      }, SCROLL_TO_DEALER_ANIMATION_MS, () => {
        this.suppressInfiniteScroll(POST_SCROLL_SUPPRESSION_MS);
      });
    }
  }

  /**
   * Fits map bounds to show all currently displayed dealers
   * Should be called when map becomes visible after being hidden
   */
  fitBoundsToDisplayedDealers() {
    if (this.displayedDealersList.length === 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();

    if (this.selectedLocation && typeof this.selectedLocation.lat === 'function') {
      bounds.extend(this.selectedLocation);
    }

    this.displayedDealersList.forEach(({ marker }) => {
      const position = marker.getPosition();
      if (position) {
        bounds.extend(position);
      }
    });

    if (!bounds.isEmpty()) {
      this.runProgrammaticMapUpdate(() => {
        this.map.fitBounds(bounds);

        const listener = google.maps.event.addListener(this.map, 'idle', () => {
          if (this.map.getZoom() > DEFAULT_ZOOM_MAX) {
            this.map.setZoom(DEFAULT_ZOOM_MAX);
          }
          google.maps.event.removeListener(listener);
        });
      });
    }
  }

  /**
   * Changes marker color
   * @param {google.maps.Marker} marker - The marker to change
   * @param {string} color - The color to change to ('red' or 'blue')
   */
  setMarkerColor(marker, color) {
    marker.setIcon(getMarkerIcon(color));
  }

  /**
   * Gets the currently active (red) marker
   * @returns {google.maps.Marker|null} The active marker or null
   */
  getActiveMarker() {
    return this.markerLocationPairs.find(pair => {
      const icon = pair.marker.getIcon();

      return icon && icon.url && icon.url.includes('D92912');
    })?.marker || null;
  }

  /**
   * Formats distance with appropriate unit
   * @param {number} distance - Distance in kilometers
   * @param {string} distanceUnit - 'km' or 'mi'
   * @returns {string} Formatted distance string
   */
  formatDistance(distance, distanceUnit) {
    if (distanceUnit === 'mi') {
      const distanceInMiles = distance * 0.621371;

      return distanceInMiles < 0.1
        ? `~${Math.round(distanceInMiles * 5280)}ft`
        : `~${distanceInMiles.toFixed(1)} ${this.distanceDisplay}`;
    }

    return distance < 1
      ? `~${Math.round(distance * 1000)}m`
      : `~${distance.toFixed(1)} ${this.distanceDisplay}`;
  }

  /**
   * Generic function to populate address in any element
   * @param {jQuery|DocumentFragment} element - The element to populate
   * @param {Object} location - The dealer location data
   */
  populateAddressGeneric(element, location) {
    const street = location.Street || '';
    const city = location.Location || '';
    const zip = location.Zip || '';
    const $el = $(element);

    $el.find('[data-dealer-field="street"]').text(street);
    $el.find('[data-dealer-field="city"]').text(city);
    $el.find('[data-dealer-field="zip"]').text(zip);
  }

  /**
   * Generic function to populate phone in any element
   * @param {jQuery|DocumentFragment} element - The element to populate
   * @param {string} phone - Phone number
   */
  populatePhoneGeneric(element, phone) {
    const $el = $(element);
    const phoneWrapper = $el.find('[data-dealer-field="phone-wrapper"]')[0];
    const hasValidPhone = phone && /\d/.test(phone);

    if (hasValidPhone) {
      const $phoneLink = $el.find('[data-dealer-field="phone-link"]');
      $phoneLink.attr('href', `tel:${phone}`);
      $el.find('[data-dealer-field="phone-text"]').text(phone);
      if (phoneWrapper) { phoneWrapper.style.display = ''; }
    } else if (phoneWrapper) {
      phoneWrapper.style.display = 'none';
    }
  }

  /**
   * Generic function to populate URL in any element
   * @param {jQuery|DocumentFragment} element - The element to populate
   * @param {string} url - Website URL
   */
  populateUrlGeneric(element, url) {
    const $el = $(element);
    const urlWrapper = $el.find('[data-dealer-field="url-wrapper"]')[0];

    if (url) {
      const $urlLink = $el.find('[data-dealer-field="url-link"]');
      $urlLink.attr('href', url);
      try {
        const urlObj = new URL(url);
        $el.find('[data-dealer-field="url-text"]').text(urlObj.hostname.replace('www.', ''));
      } catch (err) {
        $el.find('[data-dealer-field="url-text"]').text('Visit website');
      }
      if (urlWrapper) { urlWrapper.style.display = ''; }
    } else if (urlWrapper) {
      urlWrapper.style.display = 'none';
    }
  }

  /**
   * Populates dealer address fields in panel (wrapper for backward compatibility)
   * @param {jQuery} $panel - The panel element
   * @param {Object} location - The dealer location data
   */
  populateDealerAddress($panel, location) {
    this.populateAddressGeneric($panel, location);
  }

  /**
   * Populates dealer phone in panel (wrapper for backward compatibility)
   * @param {jQuery} $panel - The panel element
   * @param {Object} location - The dealer location data
   */
  populateDealerPhone($panel, location) {
    this.populatePhoneGeneric($panel, location.Phone || '');
  }

  /**
   * Populates dealer website in panel (wrapper for backward compatibility)
   * @param {jQuery} $panel - The panel element
   * @param {Object} location - The dealer location data
   */
  populateDealerWebsite($panel, location) {
    this.populateUrlGeneric($panel, location.Url || '');
  }

  /**
   * Populates dealer hours in panel
   * @param {jQuery} $panel - The panel element
   * @param {Object} hours - The dealer hours data
   */
  populateDealerHours($panel, hours) {
    const hasHoursData = hours && Object.keys(hours).some(day => hours[day] && hours[day].open && hours[day].close);
    const $hoursLabel = $panel.find('.dealer-hours-hours-label');
    const $hoursToggle = $panel.find('.dealer-hours-toggle');
    const $hoursDetails = $panel.find('.dealer-hours-details');

    if (hasHoursData) {
      const $hoursList = $panel.find('[data-dealer-field="hours-list"]');
      $hoursList.html(this.generateHoursHTML(hours));

      const statusInfo = this.isCurrentlyOpen(hours);
      const $statusEl = $panel.find('[data-dealer-field="status"]');
      const textOpen = $('.dealer-locator').attr('data-text-open') || 'Open';
      const textClosed = $('.dealer-locator').attr('data-text-closed') || 'Closed';
      $statusEl.text(statusInfo.isOpen ? textOpen : textClosed);

      $statusEl.removeClass('dealer-status--open dealer-status--closed');
      $statusEl.addClass(statusInfo.isOpen ? 'dealer-status--open' : 'dealer-status--closed');

      const days = JSON.parse($('.dealer-locator').attr('data-days') || '["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]');
      const currentDay = days[new Date().getDay()];
      const todayHours = hours[currentDay];

      if (todayHours && todayHours.open && todayHours.close) {
        $panel.find('[data-dealer-field="current-hours"]').text(`${currentDay}    ${this.formatTime(todayHours.open)}-${this.formatTime(todayHours.close)}`);
      } else {
        $panel.find('[data-dealer-field="current-hours"]').text(`${currentDay}    Closed`);
      }

      $hoursLabel.show();
      $hoursToggle.show();
      $hoursDetails.show();
    } else {
      $hoursLabel.hide();
      $hoursToggle.hide();
      $hoursDetails.hide();
    }
  }

  /**
   * Displays mobile info panel for a specific dealer
   * @param {Object} location - The location details for the dealer.
   * @param {number} distance - Distance to the dealer
   */
  showMobileInfoPanel(location, distance) {
    const $panel = $('.dealer-locator-mobile-info');

    $panel.find('[data-dealer-field="name"]').text(location.Name || 'Unknown');

    const distanceUnit = $('.dealer-locator').data('distance-unit') || 'km';
    const distanceText = this.formatDistance(distance, distanceUnit);
    $panel.find('[data-dealer-field="distance"]').text(distanceText);

    this.populateDealerAddress($panel, location);
    this.populateDealerPhone($panel, location);
    this.populateDealerWebsite($panel, location);
    this.populateDealerHours($panel, location.Hours || {});

    $panel.find('[data-dealer-content="main"]').show();
    $panel.find('[data-dealer-content="hours"]').hide();

    $panel.data('dealer-location', {
      lat: Number.parseFloat(location.Latitude),
      lng: Number.parseFloat(location.Longitude),
      name: location.Name
    });

    const markerIndex = this.markerLocationPairs.findIndex(pair => pair.location === location);
    $panel.find('.dealer-locator-get-directions-btn').attr('data-marker-index', markerIndex);
    $panel.fadeIn(300);
  }

  /**
   * Opens Google Maps with directions from user location to dealer
   * @param {number} lat - Dealer latitude
   * @param {number} lng - Dealer longitude
   */
  openDirections(lat, lng) {
    let directionsUrl;

    if (this.selectedLocation) {
      const userLat = this.selectedLocation.lat();
      const userLng = this.selectedLocation.lng();
      directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${lat},${lng}`;
    } else {
      directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }

    window.open(directionsUrl, '_blank');
  }

  /**
   * Calculates the distance between two geographic coordinates using the Haversine formula.
   * @param {number} lat1 - Latitude of the first point in degrees
   * @param {number} lon1 - Longitude of the first point in degrees
   * @param {number} lat2 - Latitude of the second point in degrees
   * @param {number} lon2 - Longitude of the second point in degrees
   * @returns {number} Distance in kilometers
   */
  getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
    const earthRadiusKm = 6371;
    const deltaLat = this.deg2rad(lat2 - lat1);
    const deltaLon = this.deg2rad(lon2 - lon1);
    const sinSquareSum =
      (Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2)) +
      ((Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2))) *
      (Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2)));
    const centralAngle = 2 * Math.atan2(Math.sqrt(sinSquareSum), Math.sqrt(1 - sinSquareSum));
    const distance = earthRadiusKm * centralAngle;

    return distance;
  }

  /**
   * Converts degrees to radians.
   * @param {number} deg - Angle in degrees
   * @returns {number} Angle in radians
   */
  deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  /**
   * Gets a Google Maps LatLng from a dealer location.
   * @param {Object} location - Dealer location data
   * @returns {google.maps.LatLng|null} Dealer coordinates or null for invalid data
   */
  getLocationLatLng(location) {
    const lat = Number.parseFloat(location.Latitude);
    const lng = Number.parseFloat(location.Longitude);

    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }

    return new google.maps.LatLng(lat, lng);
  }

  /**
   * Calculates dealer distance data from a supplied center point.
   * @param {google.maps.LatLng} center - Center used for distance calculations
   * @returns {Array} Dealers with distance values
   */
  getDealersWithDistance(center) {
    if (!center || typeof center.lat !== 'function') {
      return [];
    }

    const centerLat = center.lat();
    const centerLng = center.lng();

    return this.markerLocationPairs.map(pair => {
      const latLng = this.getLocationLatLng(pair.location);

      if (!latLng) {
        return null;
      }

      const distance = this.getDistanceFromLatLonInKm(centerLat, centerLng, latLng.lat(), latLng.lng());

      return { ...pair, distance };
    }).filter(Boolean);
  }

  /**
   * Sorts dealers by priority first, then distance from the current search center.
   * @param {Array} dealers - Dealers to sort
   * @returns {Array} Sorted dealers
   */
  sortByDistance(dealers) {
    return [...dealers].sort((dealerA, dealerB) => {
      const priorityA = Number(dealerA.location.Priority) || 0;
      const priorityB = Number(dealerB.location.Priority) || 0;

      if (priorityA !== priorityB) {
        return priorityB - priorityA;
      }

      return dealerA.distance - dealerB.distance;
    });
  }

  /**
   * Builds an area result: dealers in bounds first, optionally topped up to the minimum visible count.
   * @param {google.maps.LatLngBounds|null} bounds - Current search bounds
   * @param {Array} dealers - Dealer candidates, already filtered when filters are active
   * @returns {Array} Dealers for the current search area
   */
  getDealersForBounds(bounds, dealers) {
    const dealersByDistance = this.sortByDistance(dealers);

    if (!bounds) {
      return dealersByDistance;
    }

    const dealersInBounds = dealers
      .filter(({ location }) => {
        const latLng = this.getLocationLatLng(location);

        return latLng ? bounds.contains(latLng) : false;
      });

    const selectedDealers = this.sortByDistance(dealersInBounds);
    const minimumVisibleDealers = Math.min(this.minVisibleResults, dealers.length);

    if (!this.fillMinResults || selectedDealers.length >= minimumVisibleDealers) {
      return selectedDealers;
    }

    const selectedMarkers = new Set(selectedDealers.map(({ marker }) => marker));
    const remainingDealers = dealersByDistance.filter(({ marker }) => !selectedMarkers.has(marker));
    if (remainingDealers.length > 0) {
      const dealersNeeded = minimumVisibleDealers - selectedDealers.length;

      return selectedDealers.concat(remainingDealers.slice(0, dealersNeeded));
    }

    return selectedDealers;
  }

  /**
   * Rebuilds the current result set for the stored search area.
   */
  applyCurrentAreaDealers() {
    const center = this.searchAreaCenter || this.selectedLocation;
    const bounds = this.searchAreaBounds || this.selectedBounds;
    const allDealersWithDistance = this.getDealersWithDistance(center);
    const filteredDealers = this.applyFilters(allDealersWithDistance);

    this.currentVisibleDealers = this.getDealersForBounds(bounds, filteredDealers);
    this.filteredDealers = this.applySorting(this.currentVisibleDealers);
  }

  /**
   * Returns the dealer source currently used for rendering/pagination.
   * @returns {Array} Current dealer source
   */
  getCurrentDealersSource() {
    return this.currentSort === 'distance' ? this.currentVisibleDealers : this.filteredDealers;
  }

  /**
   * Clears rendered dealers and map markers before rendering a refreshed search.
   */
  resetRenderedDealers() {
    this.displayedDealersList = [];
    this.displayedCount = 0;
    this.isLoading = false;
    $('.dealer-locator-search-results-list').empty();
    for (const { marker } of this.markerLocationPairs) {
      this.setMarkerColor(marker, 'blue');
      marker.setMap(null);
    }
  }

  /**
   * Refreshes rendered dealers for the currently stored search area.
   * @param {number} targetCount - Number of dealers to restore after refresh
   */
  refreshRenderedDealers(targetCount = this.dealersPerPage) {
    this.resetRenderedDealers();
    this.applyCurrentAreaDealers();

    const dealersSource = this.getCurrentDealersSource();
    const dealersToLoad = Math.min(Math.max(targetCount, this.dealersPerPage, this.minVisibleResults), dealersSource.length);
    const iterations = Math.ceil(dealersToLoad / this.dealersPerPage);

    if (iterations === 0) {
      $('.dealer-locator-load-more').hide();

      return;
    }

    for (let idx = 0; idx < iterations; idx++) {
      this.loadMoreDealers();
    }
  }

  /**
   * Refreshes results using the current visible map area.
   */
  searchInCurrentMapArea() {
    if (!this.map || !this.map.getBounds() || !this.map.getCenter()) {
      return;
    }

    this.searchAreaBounds = this.map.getBounds();
    this.searchAreaCenter = this.map.getCenter();
    this.selectedBounds = this.searchAreaBounds;
    this.selectedLocation = this.searchAreaCenter;
    this.hasActiveSearch = true;
    this.hideSearchAreaButton();

    this.refreshRenderedDealers();

    if (!isMobileViewport()) {
      this.activateFirstDealer();
    }
  }

  /**
   * Displays search results by focusing the map on the selected location at Zoom 13.
   * Uses the selected area first and tops up sparse areas with the nearest dealers.
   */
  displaySearchResults() {
    if (!this.selectedLocation || typeof this.selectedLocation.lat !== 'function') {
      return;
    }

    this.createMarkers(false);
    this.searchAreaCenter = this.selectedLocation;
    this.searchAreaBounds = this.selectedBounds;
    this.activeFilters = [];
    this.currentSort = 'distance';
    this.hasActiveSearch = true;
    this.hideSearchAreaButton();
    $('.dealer-locator-filter-panel input[name="filter"]').prop('checked', false);
    $('.dealer-locator-active-filters').empty();
    $('.dealer-locator-sort-option').removeClass('dealer-locator-sort-option--active');
    $('.dealer-locator-sort-option[data-sort="distance"]').addClass('dealer-locator-sort-option--active');
    $('.dealer-locator-sort-value').text($('.dealer-locator-sort-option[data-sort="distance"]').data('sort-label'));

    this.refreshRenderedDealers();
    this.initInfiniteScroll();
    this.attachFilterAndSortEvents();

    if (isMobileViewport()) {
      $('.dealer-locator-options').hide();
      $('.dealer-locator-search-results').show();
      $('.dealer-locator-search-results-list').addClass('mobile-hidden');
      $('.dealer-locator-load-more-container').hide();
      $('.dealer-locator-map').removeClass('mobile-hidden');
      $('.dealer-locator-mobile-result-selector').addClass('is-visible');
      $('.dealer-locator-show-map').addClass('dealer-locator-result-selector-btn--active');
      $('.dealer-locator-show-results').removeClass('dealer-locator-result-selector-btn--active');
      // Disable sort on mobile when map view is initially shown (timeout ensures DOM is ready)
      setTimeout(() => {
        $('.dealer-locator-sort').addClass('dealer-locator-sort--disabled');
      }, 0);
      // On mobile: all dealer markers stay blue, no dealer is pre-selected
    } else {
      $('.dealer-locator-options').fadeOut(() => {
        $('.dealer-locator-search-results').fadeIn();
        this.activateFirstDealer();
      });
    }
  }

  /**
   * Loads and displays additional dealers on the map and in the results list.
   *
   * This method handles pagination of dealer results, manages map markers visibility,
   * adjusts map bounds to fit all displayed dealers, and controls the "Load More" button visibility.
   * On mobile devices (width <= 992px), it automatically switches to the results view.
   *
   * @returns {void} Returns early if no more dealers are available to display.
   */
  loadMoreDealers() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;

    const dealersSource = this.getCurrentDealersSource();
    const startIndex = this.displayedCount;
    const endIndex = Math.min(startIndex + this.dealersPerPage, dealersSource.length);
    const dealersToShow = dealersSource.slice(startIndex, endIndex);

    if (dealersToShow.length === 0) {
      this.isLoading = false;

      return;
    }

    this.displayedDealersList.push(...dealersToShow);

    if (this.displayedCount === 0) {
      this.markerLocationPairs.forEach(({ marker }) => marker.setMap(null));
    }

    const bounds = new google.maps.LatLngBounds();

    if (this.selectedLocation && typeof this.selectedLocation.lat === 'function') {
      bounds.extend(this.selectedLocation);
    }

    dealersToShow.forEach(({ marker }) => {
      marker.setMap(this.map);
    });

    this.displayedDealersList.forEach(({ marker }) => {
      const position = marker.getPosition();
      if (position) {
        bounds.extend(position);
      }
    });

    const isMapHidden = $('.dealer-locator-map').hasClass('mobile-hidden');
    if (!isMapHidden && !bounds.isEmpty()) {
      this.runProgrammaticMapUpdate(() => {
        this.map.fitBounds(bounds);

        const listener = google.maps.event.addListener(this.map, 'idle', () => {
          if (this.map.getZoom() > DEFAULT_ZOOM_MAX) { this.map.setZoom(DEFAULT_ZOOM_MAX); }
          google.maps.event.removeListener(listener);
        });
      });
    }

    this.appendSearchResults(dealersToShow);
    this.displayedCount = endIndex;
    this.isLoading = false;

    const $loadMoreBtn = $('.dealer-locator-load-more');
    if (this.displayedCount < dealersSource.length) {
      $loadMoreBtn.show();
    } else {
      $loadMoreBtn.hide();
    }

    if (isMobileViewport()) {
      $('.dealer-locator-mobile-result-selector').show();
      $('.dealer-locator-show-map').addClass('dealer-locator-result-selector-btn--active');
      $('.dealer-locator-show-results').removeClass('dealer-locator-result-selector-btn--active');
    }
  }

  /**
   * Populates a single dealer list item with location data
   * @param {DocumentFragment} listItem - The cloned template item
   * @param {Object} location - The dealer location data
   * @param {number} distance - Distance to the dealer in km
   * @param {number} markerIndex - Index of the marker in markerLocationPairs
   * @param {string} distanceUnit - Distance unit ('km' or 'mi')
   */
  // eslint-disable-next-line max-params
  populateDealerListItem(listItem, location, distance, markerIndex, distanceUnit) {
    const name = location.Name || 'Unknown';
    const phone = location.Phone || '';
    const url = location.Url || '';
    const hours = location.Hours || {};

    listItem.querySelector('[data-dealer-field="name"]').textContent = name;

    this.populateAddressGeneric(listItem, location);
    this.populateListItemDistance(listItem, distance, distanceUnit);
    this.populateListItemStatus(listItem, hours);
    this.populatePhoneGeneric(listItem, phone);
    this.populateUrlGeneric(listItem, url);
    this.populateListItemHours(listItem, hours);

    listItem.querySelector('.dealer-locator-show-on-map-btn').setAttribute('data-marker-index', markerIndex);
    listItem.querySelector('.dealer-locator-get-directions-btn').setAttribute('data-marker-index', markerIndex);
  }

  /**
   * Populates distance field in list item
   * @param {DocumentFragment} listItem - The list item
   * @param {number} distance - Distance in km
   * @param {string} distanceUnit - 'km' or 'mi'
   */
  populateListItemDistance(listItem, distance, distanceUnit) {
    const distanceEl = listItem.querySelector('[data-dealer-field="distance"]');
    if (distance !== undefined) {
      distanceEl.textContent = this.formatDistance(distance, distanceUnit);
    }
  }

  /**
   * Populates status and current hours in list item
   * @param {DocumentFragment} listItem - The list item
   * @param {Object} hours - Dealer hours data
   */
  populateListItemStatus(listItem, hours) {
    const statusInfo = this.isCurrentlyOpen(hours);
    const statusEl = listItem.querySelector('[data-dealer-field="status"]');
    const textOpen = $('.dealer-locator').attr('data-text-open') || 'Open';
    const textClosed = $('.dealer-locator').attr('data-text-closed') || 'Closed';

    statusEl.textContent = statusInfo.isOpen ? textOpen : textClosed;
    statusEl.classList.add(statusInfo.isOpen ? 'dealer-status--open' : 'dealer-status--closed');

    const days = JSON.parse($('.dealer-locator').attr('data-days') || '["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]');
    const currentDay = days[new Date().getDay()];
    const todayHours = hours[currentDay];
    const currentHoursEl = listItem.querySelector('[data-dealer-field="current-hours"]');

    if (todayHours && todayHours.open && todayHours.close) {
      currentHoursEl.textContent = `${currentDay}    ${this.formatTime(todayHours.open)}-${this.formatTime(todayHours.close)}`;
    } else {
      currentHoursEl.textContent = `${currentDay}    Closed`;
    }
  }



  /**
   * Populates hours section in list item
   * @param {DocumentFragment} listItem - The list item
   * @param {Object} hours - Dealer hours data
   */
  populateListItemHours(listItem, hours) {
    const hasHoursData = hours && Object.keys(hours).some(day => hours[day] && hours[day].open && hours[day].close);
    const hoursLabelEl = listItem.querySelector('.dealer-hours-hours-label');
    const hoursToggleEl = listItem.querySelector('.dealer-hours-toggle');
    const hoursDetailsEl = listItem.querySelector('.dealer-hours-details');

    if (hasHoursData) {
      const hoursListEl = listItem.querySelector('[data-dealer-field="hours-list"]');
      hoursListEl.innerHTML = this.generateHoursHTML(hours);
    } else {
      if (hoursLabelEl) { hoursLabelEl.remove(); }
      if (hoursToggleEl) { hoursToggleEl.remove(); }
      if (hoursDetailsEl) { hoursDetailsEl.remove(); }
    }
  }

  /**
   * Appends dealer list items to the results list
   * @param {Array} dealersToShow - Array of dealers to display
   */
  appendSearchResults(dealersToShow) {
    const $resultsList = $('.dealer-locator-search-results-list');

    if (this.displayedCount === 0) {
      $resultsList.empty();
    }

    const distanceUnit = $('.dealer-locator').data('distance-unit') || 'km';

    const template = document.getElementById('dealer-list-item-template');
    if (!template) {
      return;
    }

    dealersToShow.forEach(({ marker, location, distance }) => {
      const markerIndex = this.markerLocationPairs.findIndex(pair => pair.marker === marker);
      const listItem = template.content.cloneNode(true);

      this.populateDealerListItem(listItem, location, distance, markerIndex, distanceUnit);
      $resultsList.append(listItem);
    });
  }

  /**
   * Initialize all event listeners for dealer list interactions (called once in constructor)
   * Uses event delegation to handle dynamically added elements
   */
  initDealerListEventListeners() {
    // eslint-disable-next-line consistent-this
    const self = this;
    const $resultsList = $('.dealer-locator-search-results-list');

    $resultsList.off('click', '[data-dealer-toggle="main"]').on('click', '[data-dealer-toggle="main"]', function(e) {
      e.preventDefault();
      e.stopPropagation();

      const $item = $(this).closest('.dealer-list-item');
      const $content = $item.find('[data-dealer-content="main"]');
      const $chevron = $(this).find('.dealer-chevron');
      const isCurrentlyOpen = $item.hasClass('dealer-list-item--active');
      const markerIndex = $item.find('.dealer-locator-view-marker-btn').first().data('marker-index');

      const $openItems = $('.dealer-list-item--active');

      $openItems.each((index, el) => {
        const $otherItem = $(el);
        $otherItem.removeClass('dealer-list-item--active');
        $otherItem.find('[data-dealer-content="main"]').slideUp(300);
        $otherItem.find('.dealer-chevron').removeClass('dealer-chevron--open');

        if (!isMobileViewport()) {
          const otherIdx = $otherItem.find('.dealer-locator-view-marker-btn').first().data('marker-index');
          if (otherIdx !== undefined && self.markerLocationPairs[otherIdx]) {
            self.setMarkerColor(self.markerLocationPairs[otherIdx].marker, 'blue');
          }
        }
      });

      if (!isCurrentlyOpen) {
        $item.addClass('dealer-list-item--active');
        $content.slideDown(300);
        $chevron.addClass('dealer-chevron--open');

        if (!isMobileViewport() && markerIndex !== undefined && self.markerLocationPairs[markerIndex]) {
          self.setMarkerColor(self.markerLocationPairs[markerIndex].marker, 'red');
        }
      }
    });

    // Hours toggle - also bound to results list
    $resultsList.off('click', '[data-dealer-toggle="hours"]').on('click', '[data-dealer-toggle="hours"]', function(e) {
      e.preventDefault();
      e.stopPropagation();

      const $item = $(this).closest('.dealer-list-item');
      const $content = $item.find('[data-dealer-content="hours"]');
      const $chevron = $(this).find('.dealer-chevron');

      $content.slideToggle(300);
      $chevron.toggleClass('dealer-chevron--open');
    });

    // Show on map button - bound to results list
    $resultsList.off('click', '.dealer-locator-show-on-map-btn').on('click', '.dealer-locator-show-on-map-btn', function(e) {
      e.preventDefault();
      e.stopPropagation();

      const markerIndex = $(this).data('marker-index');

      if (markerIndex === undefined || markerIndex < 0 || !self.markerLocationPairs[markerIndex]) {
        return;
      }

      const { marker, location } = self.markerLocationPairs[markerIndex];
      const dealerData = self.currentVisibleDealers.find(dealer => dealer.location === location);
      const distance = dealerData ? dealerData.distance : 0;

      self.markerLocationPairs.forEach(pair => {
        self.setMarkerColor(pair.marker, 'blue');
      });

      self.setMarkerColor(marker, 'red');

      self.runProgrammaticMapUpdate(() => {
        self.map.setCenter(marker.getPosition());
        self.map.setZoom(15);
      });

      if (isMobileViewport()) {
        $('.dealer-locator-search-results-list').addClass('mobile-hidden');
        $('.dealer-locator-load-more-container').hide();
        $('.dealer-locator-map').removeClass('mobile-hidden');
        $('.dealer-locator-show-map').addClass('dealer-locator-result-selector-btn--active');
        $('.dealer-locator-show-results').removeClass('dealer-locator-result-selector-btn--active');

        self.showMobileInfoPanel(location, distance);
      }
    });

    // Get directions button - use document because it's also in mobile panel
    $(document).off('click.dealerDirections', '.dealer-locator-get-directions-btn').on('click.dealerDirections', '.dealer-locator-get-directions-btn', function() {
      const markerIndex = $(this).closest('[data-marker-index]').data('marker-index');

      if (markerIndex === undefined || markerIndex < 0 || !self.markerLocationPairs[markerIndex]) {
        return;
      }

      const { location } = self.markerLocationPairs[markerIndex];
      const lat = Number.parseFloat(location.Latitude);
      const lng = Number.parseFloat(location.Longitude);

      self.openDirections(lat, lng);
    });
  }

  /**
   * Checks if dealer is currently open based on hours
   * @param {Object} hours - The dealer hours data object
   * @returns {Object} Object containing isOpen boolean and closingTime string
   */
  isCurrentlyOpen(hours) {
    const now = new Date();
    const days = JSON.parse($('.dealer-locator').attr('data-days') || '["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]');
    const currentDay = days[now.getDay()];
    const currentTime = (now.getHours() * 60) + now.getMinutes();

    const todayHours = hours[currentDay];

    if (!todayHours || !todayHours.open || !todayHours.close) {
      return { isOpen: false, closingTime: null };
    }

    const [openHour, openMin] = todayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = todayHours.close.split(':').map(Number);

    const openTime = (openHour * 60) + openMin;
    const closeTime = (closeHour * 60) + closeMin;

    const isOpen = currentTime >= openTime && currentTime < closeTime;

    return {
      isOpen,
      closingTime: isOpen ? this.formatTime(todayHours.close) : null 
    };
  }

  /**
   * Formats time from 24-hour format to 12-hour format with am/pm.
   * @param {string} time24 - Time in 24-hour format (e.g., "14:30" or "09:00")
   * @returns {string} Formatted time in 12-hour format (e.g., "2:30pm" or "9am")
   */
  formatTime(time24) {
    if (!time24) { return ''; }
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'pm' : 'am';
    const hours12 = hours % 12 || 12;

    return `${hours12}${minutes > 0 ? ':' + minutes.toString().padStart(2, '0') : ''}${period}`;
  }

  /**
   * Generates hours list HTML using translated day names from data-days attribute
   * @param {Object} hours - Object containing hours for each day of the week
   * @returns {string} HTML string with formatted hours for each day
   */
  generateHoursHTML(hours) {
    const daysFromSunday = JSON.parse($('.dealer-locator').attr('data-days') || '["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]');
    const daysDisplayOrder = [
      daysFromSunday[1], // Monday
      daysFromSunday[2], // Tuesday
      daysFromSunday[3], // Wednesday
      daysFromSunday[4], // Thursday
      daysFromSunday[5], // Friday
      daysFromSunday[6], // Saturday
      daysFromSunday[0]  // Sunday
    ];
    const now = new Date();
    const currentDay = daysFromSunday[now.getDay()];

    return daysDisplayOrder.map(day => {
      const dayHours = hours[day];
      const isToday = day === currentDay;
      const hoursText = (dayHours && dayHours.open && dayHours.close) 
        ? `${this.formatTime(dayHours.open)}-${this.formatTime(dayHours.close)}`
        : 'Closed';

      return `
        <div class="dealer-hours-row ${isToday ? 'dealer-hours-row--today' : ''}">
          <span class="dealer-hours-day">${day}</span>
          <span class="dealer-hours-time">${hoursText}</span>
        </div>
      `;
    }).join('');
  }

  /**
   * Applies filters to dealers using AND logic
   * Works only on currently displayed dealers
   * If filter property doesn't exist on dealer, dealer will NOT pass the filter
   * @param {Array} dealers - Array of dealer objects to filter
   * @returns {Array} Filtered array of dealers
   */
  applyFilters(dealers) {
    if (this.activeFilters.length === 0) {
      return dealers;
    }

    return dealers.filter(({ location }) => {
      return this.activeFilters.every(filter => {
        // Dealer must have the property AND it must be exactly true
        // If property doesn't exist or is not true, dealer fails this filter
        return location[filter.value] === true;
      });
    });
  }

  /**
   * Applies sorting to dealers
   * @param {Array} dealers - Array of dealer objects to sort
   * @returns {Array} Sorted array of dealers
   */
  applySorting(dealers) {
    const sortedDealers = [...dealers];

    if (this.currentSort === 'alphabetical') {
      sortedDealers.sort((dealerA, dealerB) => {
        const nameA = (dealerA.location.Name || '').toLowerCase();
        const nameB = (dealerB.location.Name || '').toLowerCase();

        return nameA.localeCompare(nameB);
      });
    } else {
      return this.sortByDistance(dealers);
    }

    return sortedDealers;
  }

  /**
   * Applies both filters and sorting to all available dealers
   */
  applyFiltersAndSort() {
    if (this.searchAreaCenter || this.selectedLocation) {
      this.applyCurrentAreaDealers();

      return;
    }

    this.filteredDealers = this.applySorting(this.applyFilters(this.currentVisibleDealers));
  }

  /**
   * Updates the displayed dealers based on current filters
   * Maintains the current number of displayed dealers
   */
  updateDisplayedDealersWithFilter() {
    const previousDisplayedCount = this.displayedCount;

    this.refreshRenderedDealers(previousDisplayedCount > 0 ? previousDisplayedCount : this.dealersPerPage);
  }

  /**
   * Re-sorts only the currently displayed dealers without loading new ones
   */
  updateDisplayedDealersWithSort() {
    // Reset all markers to blue before re-rendering
    this.markerLocationPairs.forEach(({ marker }) => this.setMarkerColor(marker, 'blue'));

    this.applyFiltersAndSort();

    const sortedDisplayedDealers = this.applySorting(this.displayedDealersList);
    $('.dealer-locator-search-results-list').empty();
    this.markerLocationPairs.forEach(({ marker }) => marker.setMap(null));
    const bounds = new google.maps.LatLngBounds();

    if (this.selectedLocation && typeof this.selectedLocation.lat === 'function') {
      bounds.extend(this.selectedLocation);
    }

    sortedDisplayedDealers.forEach(({ marker }) => {
      marker.setMap(this.map);
      bounds.extend(marker.getPosition());
    });

    const isMapHidden = $('.dealer-locator-map').hasClass('mobile-hidden');
    if (!bounds.isEmpty() && !isMapHidden) {
      this.runProgrammaticMapUpdate(() => {
        this.map.fitBounds(bounds);
      });
    }

    this.appendSearchResults(sortedDisplayedDealers);
    this.displayedDealersList = sortedDisplayedDealers;

    const dealersSource = this.getCurrentDealersSource();
    const $loadMoreBtn = $('.dealer-locator-load-more');
    if (this.displayedCount < dealersSource.length) {
      $loadMoreBtn.show();
    } else {
      $loadMoreBtn.hide();
    }
  }

  /**
   * Renders active filter tags
   */
  renderFilterTags() {
    const $container = $('.dealer-locator-active-filters');
    $container.empty();

    this.activeFilters.forEach(filter => {
      const $tag = $(`
        <span class="dealer-locator-filter-tag">
          <span class="dealer-locator-filter-tag-label">${filter.label}</span>
          <button class="dealer-locator-filter-tag-remove" data-filter="${filter.value}">✕</button>
        </span>
      `);
      $container.append($tag);
    });
  }

  /**
   * Removes a filter
   * @param {string} filterValue - The value of the filter to remove
   */
  removeFilter(filterValue) {
    const index = this.activeFilters.findIndex(filter => filter.value === filterValue);
    if (index > -1) {
      this.activeFilters.splice(index, 1);
      $(`.dealer-locator-filter-panel input[value="${filterValue}"]`).prop('checked', false);
      this.renderFilterTags();
      this.updateDisplayedDealersWithFilter();
    }
  }

  /**
   * Opens filter panel
   */
  openFilterPanel() {
    $('.dealer-locator-filter-panel').addClass('is-open');
    $('.dealer-locator-filter-backdrop').addClass('is-visible');
  }

  /**
   * Closes filter panel
   */
  closeFilterPanel() {
    $('.dealer-locator-filter-panel').removeClass('is-open');
    $('.dealer-locator-filter-backdrop').removeClass('is-visible');
  }

  /**
   * Applies filters from panel
   */
  applyFiltersFromPanel() {
    this.activeFilters = [];
    $('.dealer-locator-filter-panel input[name="filter"]:checked').each((index, el) => {
      const $checkbox = $(el);
      this.activeFilters.push({
        value: $checkbox.val(),
        label: $checkbox.closest('.dealer-locator-filter-checkbox').find('.dealer-locator-filter-checkbox-label').text()
      });
    });
    this.renderFilterTags();
    this.closeFilterPanel();
    this.updateDisplayedDealersWithFilter();
  }

  /**
   * Resets all filters
   */
  resetFilters() {
    this.activeFilters = [];
    $('.dealer-locator-filter-panel input[name="filter"]').prop('checked', false);
    this.renderFilterTags();
    this.closeFilterPanel();
    this.updateDisplayedDealersWithFilter();
  }

  /**
   * Sets the sort option
   * @param {string} sortType - The type of sort to apply ('distance' or 'alphabetical')
   */
  setSort(sortType) {
    this.currentSort = sortType;
    $('.dealer-locator-sort-option').removeClass('dealer-locator-sort-option--active');
    const $activeOption = $(`.dealer-locator-sort-option[data-sort="${sortType}"]`);
    $activeOption.addClass('dealer-locator-sort-option--active');
    $('.dealer-locator-sort-value').text($activeOption.data('sort-label') || $activeOption.text().trim());
    $('.dealer-locator-sort-trigger').removeClass('is-open');
    $('.dealer-locator-sort-options').removeClass('is-open');
    this.updateDisplayedDealersWithSort();
  }

  /**
   * Loads dealer batches until the specified marker's dealer is in the DOM list
   * @param {number} markerIndex - Index of the marker in markerLocationPairs
   */
  loadUntilDealer(markerIndex) {
    const dealersSource = this.getCurrentDealersSource();
    const dealerEntry = dealersSource.find(dealer => {
      return this.markerLocationPairs.findIndex(pair => pair.marker === dealer.marker) === markerIndex;
    });

    if (!dealerEntry) {
      return;
    }

    const dealerSourceIndex = dealersSource.indexOf(dealerEntry);

    while (this.displayedCount <= dealerSourceIndex) {
      const prevCount = this.displayedCount;
      this.loadMoreDealers();
      if (this.displayedCount === prevCount) {
        break;
      }
    }

    this.expandDealerInList(markerIndex);
  }

  /**
   * Attaches scroll-based infinite loading to the results list (called once per session)
   */
  initInfiniteScroll() {
    if (this.infiniteScrollAttached) {
      return;
    }
    this.infiniteScrollAttached = true;

    const $list = $('.dealer-locator-search-results-list');
    $list.on('scroll.infiniteScroll', () => {
      if (this.suppressInfiniteScrollLoad) {
        return;
      }

      const el = $list[0];
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
        this.loadMoreDealers();
      }
    });
  }

  /**
   * Attaches filter and sort event handlers
   */
  attachFilterAndSortEvents() {
    if (this.filterEventsAttached) {
      return;
    }

    if ($('.dealer-locator-sort-trigger').length === 0) {
      return;
    }

    this.filterEventsAttached = true;

    $('.dealer-locator-sort-trigger').off('click').on('click', (e) => {
      e.stopPropagation();

      if (isMobileViewport() && !$('.dealer-locator-map').hasClass('mobile-hidden')) {
        return;
      }

      const $trigger = $(e.currentTarget);
      const $dropdown = $('.dealer-locator-sort-options');
      const $toolbar = $('.dealer-locator-toolbar');
      const isOpen = $dropdown.hasClass('is-open');

      $trigger.toggleClass('is-open');
      $dropdown.toggleClass('is-open');

      $trigger.attr('aria-expanded', !isOpen);

      if (!isOpen) {
        const toolbarRect = $toolbar[0].getBoundingClientRect();
        $dropdown.css({
          top: toolbarRect.bottom + 'px',
          left: toolbarRect.left + 'px',
          right: 'auto',
          width: toolbarRect.width + 'px'
        });
      }
    });

    // Sort option selection
    $('.dealer-locator-sort-option').off('click').on('click', (e) => {
      const sortType = $(e.currentTarget).data('sort');
      this.setSort(sortType);
    });

    // Close sort dropdown when clicking outside
    $(document).off('click.sortDropdown').on('click.sortDropdown', (e) => {
      if (!$(e.target).closest('.dealer-locator-sort-dropdown').length) {
        $('.dealer-locator-sort-trigger').removeClass('is-open').attr('aria-expanded', 'false');
        $('.dealer-locator-sort-options').removeClass('is-open');
      }
    });

    // Filter panel open
    $('.dealer-locator-filter-btn').off('click').on('click', () => {
      this.openFilterPanel();
    });

    // Filter panel close
    $('.dealer-locator-filter-panel-close').off('click').on('click', () => {
      this.closeFilterPanel();
    });

    // Apply filters
    $('.dealer-locator-filter-apply').off('click').on('click', () => {
      this.applyFiltersFromPanel();
    });

    // Reset filters
    $('.dealer-locator-filter-reset').off('click').on('click', () => {
      this.resetFilters();
    });

    // Close filter panel when clicking on backdrop
    $('.dealer-locator-filter-backdrop').off('click').on('click', () => {
      this.closeFilterPanel();
    });

    // Remove filter tag
    $(document).off('click.filterTag').on('click.filterTag', '.dealer-locator-filter-tag-remove', (e) => {
      e.stopPropagation();
      const filterName = $(e.currentTarget).data('filter');
      this.removeFilter(filterName);
    });
  }

  /**
   * Displays the search results list.
   * @param {Array<Object>} filteredMarkers - The filtered markers to display.
   */

  attachEvents() {
    // Attach filter and sort events immediately
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      this.attachFilterAndSortEvents();
    }, 100);

    // Clear button: show/hide icon vs clear button based on input value
    $('.dealer-locator-search-input').on('input', function() {
      const hasValue = $(this).val().length > 0;
      $('.dealer-locator-search-input-wrapper .dealer-locator-search-icon').toggle(!hasValue);
      $('.dealer-locator-search-clear').toggleClass('is-active', hasValue);
    });

    // Clear button click: reset input and restore search icon
    $('.dealer-locator-search-clear').on('click', () => {
      const $input = $('.dealer-locator-search-input');
      $input.val('');
      this.selectedLocation = null;
      this.selectedBounds = null;
      this.geolocationRequested = false;
      $('.dealer-locator-search-input-wrapper .dealer-locator-search-icon').show();
      $('.dealer-locator-search-clear').removeClass('is-active');
      $input.focus();
    });

    // Search button click
    $('.dealer-locator-search').on('click', () => {
      this.displaySearchResults();
    });

    // Enter key in search input
    // Our listener is registered before Google's (attachEvents runs in constructor, initAutocomplete runs later),
    // so stopImmediatePropagation() here will prevent Google's keydown handler from firing.
    // - Dropdown not visible: trigger search (geolocation or previously selected place)
    // - Dropdown visible + item highlighted (arrow key nav): let Google handle Enter to select it
    // - Dropdown visible + nothing highlighted: block Google from closing dropdown, user must pick a suggestion
    $('.dealer-locator-search-input').on('keydown', (e) => {
      if (e.key === 'Enter') {
        const pacContainerVisible = $('.pac-container:visible').length > 0;
        if (pacContainerVisible) {
          const hasHighlightedItem = $('.pac-item-selected').length > 0;
          if (!hasHighlightedItem) {
            e.preventDefault();
            e.stopImmediatePropagation(); // Prevent Google from closing the dropdown
          }
          // If an item is highlighted, let Google handle Enter to select it (place_changed will fire)
        } else {
          e.preventDefault();
          this.displaySearchResults();
        }
      }
    });

    // Load more
    $('.dealer-locator-load-more').on('click', () => {
      this.loadMoreDealers();
    });

    // Search current visible map area
    $('.dealer-locator-search-area-btn').on('click', () => {
      this.searchInCurrentMapArea();
    });

    // Back button to new search
    $('.dealer-locator-back-btn').on('click', () => {
      $('.dealer-locator-search-results').fadeOut(300, () => {
        $('.dealer-locator-options').fadeIn(300);

        this.clearMarkers();
        this.selectedLocation = null;
        this.selectedBounds = null;
        this.searchAreaCenter = null;
        this.searchAreaBounds = null;
        this.hasActiveSearch = false;
        this.hideSearchAreaButton();
        $('.dealer-locator-search-input').val('');
        $('.dealer-locator-search-input-wrapper .dealer-locator-search-icon').show();
        $('.dealer-locator-search-clear').removeClass('is-active');
        $('.dealer-locator-load-more').hide();

        // Mobile reset
        $('.dealer-locator-mobile-result-selector').removeClass('is-visible');
        $('.dealer-locator-map').removeClass('mobile-hidden');
        $('.dealer-locator-search-results-list').removeClass('mobile-hidden');
        $('.dealer-locator_show-results').removeClass('dealer-locator-result-selector-btn--active');
        $('.dealer-locator-show-map').removeClass('dealer-locator-result-selector-btn--active');
        $('.dealer-locator-mobile-info').hide();

        if (this.userLocationMarker) {
          this.userLocationMarker.setMap(null);
          this.userLocationMarker = null;
        }

        this.geolocationRequested = false;
        this.currentVisibleDealers = [];
        this.displayedDealersList = [];
        this.filteredDealers = [];
        this.displayedCount = 0;
        this.activeFilters = [];
        this.currentSort = 'distance';
        $('.dealer-locator-filter-panel input[name="filter"]').prop('checked', false);
        $('.dealer-locator-active-filters').empty();
        $('.dealer-locator-sort-option').removeClass('dealer-locator-sort-option--active');
        $('.dealer-locator-sort-option[data-sort="distance"]').addClass('dealer-locator-sort-option--active');
        $('.dealer-locator-sort-value').text($('.dealer-locator-sort-option[data-sort="distance"]').data('sort-label'));
        $('.dealer-locator-filter-panel').removeClass('is-open');
        $('.dealer-locator-filter-backdrop').removeClass('is-visible');
      });
    });

    // Mobile: Show map button
    $('.dealer-locator-show-map').on('click', () => {
      if (isMobileViewport()) {
        $('.dealer-locator-search-results-list').addClass('mobile-hidden');
        $('.dealer-locator-load-more-container').hide();
        $('.dealer-locator-mobile-info').hide();
        $('.dealer-locator-map').removeClass('mobile-hidden');
        $('.dealer-locator-sort').addClass('dealer-locator-sort--disabled');
        $('.dealer-locator-show-map').addClass('dealer-locator-result-selector-btn--active');
        $('.dealer-locator-show-results').removeClass('dealer-locator-result-selector-btn--active');

        // Fit bounds after map becomes visible (slight delay to ensure map is rendered)
        setTimeout(() => {
          this.fitBoundsToDisplayedDealers();
        }, 100);
      }
    });

    // Mobile: Show results button
    $('.dealer-locator-show-results').on('click', () => {
      if (isMobileViewport()) {
        // Hide map, show results
        $('.dealer-locator-map').addClass('mobile-hidden');
        $('.dealer-locator-mobile-info').hide();
        this.hideSearchAreaButton();
        $('.dealer-locator-search-results-list').removeClass('mobile-hidden');
        $('.dealer-locator-load-more-container').show();
        $('.dealer-locator-sort').removeClass('dealer-locator-sort--disabled');

        // Update buttons
        $('.dealer-locator-show-results').addClass('dealer-locator-result-selector-btn--active');
        $('.dealer-locator-show-map').removeClass('dealer-locator-result-selector-btn--active');

        // Expand the currently active (red marker) dealer in the list
        const activeMarker = this.getActiveMarker();
        if (activeMarker) {
          const markerIndex = this.markerLocationPairs.findIndex(pair => pair.marker === activeMarker);
          if (markerIndex !== -1) {
            setTimeout(() => {
              this.expandDealerInList(markerIndex);
            }, 100);
          }
        }
      }
    });

    // Mobile info panel: Close button
    $('.dealer-mobile-info-close').on('click', () => {
      $('.dealer-locator-mobile-info').fadeOut(300);

      const activeMarker = this.getActiveMarker();
      if (activeMarker) {
        this.setMarkerColor(activeMarker, 'blue');
      }
    });

    // Mobile info panel: Hours toggle
    $(document).off('click.mobileHours', '.dealer-locator-mobile-info [data-dealer-toggle="hours"]').on('click.mobileHours', '.dealer-locator-mobile-info [data-dealer-toggle="hours"]', function(e) {
      e.preventDefault();
      e.stopPropagation();

      const $toggle = $(this);
      const $hoursDetails = $toggle.siblings('[data-dealer-content="hours"]');
      const $chevron = $toggle.find('.dealer-chevron');

      $hoursDetails.slideToggle(300);
      $chevron.toggleClass('dealer-chevron--open');
    });
  }
}

/**
 * Initializes the Dealer Locator Map functionality when the document is ready.
 */
$(document).ready(() => {
  if (!DealerLocatorMap.isOnPage()) {
    return;
  }

  /**
   * Dynamically loads the Google Maps API script if it has not been loaded already.
   * @param {string} googleMapsKey - The API key for Google Maps.
   */
  const loadGoogleMapsAPI = (googleMapsKey) => {
    if (!window.dlrLocatorInitMap) {
      function initMap() {
        const mapInstance = new DealerLocatorMap();
        mapInstance.initMap();
      }

      window.dlrLocatorInitMap = initMap;

      // Create and append the Google Maps API script
      let script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsKey}&libraries=places,geometry&callback=dlrLocatorInitMap&loading=async`;
      document.body.appendChild(script);
    }
  }

  /**
   * Fetches the Google Maps API key from the server and loads the API.
   */
  const fetchApiKey = () => {
    const apiRoot = $('.dealer-locator').data('api-root') || '';

    $.ajax({
      url: `${apiRoot}/apikey`,
      method: 'GET',
      dataType: 'json',
      headers: {
        'Content-Type': 'application/json',
      }
    })
      .done((data) => {
        if (data.GoogleMapsApiKey) {
          loadGoogleMapsAPI(data.GoogleMapsApiKey);
        }
      })
  }

  fetchApiKey();
});
